import React, { useMemo, useState } from "react";
import { Badge } from "../ui/badge";
import { Order, OrderStatus } from "@/redux/services/order/order.type";
import {
  cn,
  formatDate,
  formatPrice,
  getOrderActionText,
  getOrderStatusText,
  getOrderTotal,
  STATUS_BUTTON_STYLES,
  getOrderTypeLabel,
} from "@/lib/utils";
import {
  OrderApi,
  useChangeOrderStatusMutation,
} from "@/redux/services/order/order.api";
import { motion } from "framer-motion";
import { highlightText } from "@/lib/highlightText";
import { useAppDispatch, useAppSelector } from "@/redux/features/hooks";
import { Roles } from "@/redux/services/user/user.type";
import { useRestaurantCurrency } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { ORDERS_MANAGE_PERMISSIONS } from "@/lib/navigation";
import { useGetOpenSessionQuery } from "@/redux/services/pos/registerSession.api";
import { toast } from "react-hot-toast";
import { Bike, Clock, Package, CreditCard } from "lucide-react";
import OrderDetailsDrawer from "./OrderDetailsDrawer";
import { logger } from "@/services/Logger";
import { Button } from "../ui/button";
import PaymentModal from "@/components/pos/payment/PaymentModal";
import {
  formatDuration,
  useOrderTimingBreakdown,
} from "@/hooks/useOrderTimingBreakdown";
import { useOrderCloseConfirm } from "@/hooks/useOrderCloseConfirm";
import {
  isCollectedMoneyViolation,
  useCollectedMoneyCancelBlock,
} from "@/hooks/useCollectedMoneyCancelBlock";

const MAX_PREVIEW_ITEMS = 3;

interface Props {
  order: Order;
  searchOrderNumber: string;
  readonly?: boolean;
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

const OrderCard: React.FC<Props> = ({
  order,
  searchOrderNumber,
  readonly = false,
  onModalOpen,
  onModalClose,
}) => {
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const currentRestaurant = useAppSelector(
    (state) => state.user.currentRestaurant
  );
  const dispatch = useAppDispatch();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [changeOrderStatus, { isLoading: isChangingStatus }] =
    useChangeOrderStatusMutation();
  const confirmStatusChange = useOrderCloseConfirm();
  const { blockIfCollected, showBlocked } = useCollectedMoneyCancelBlock();
  const currency = useRestaurantCurrency();
  const { can, canAny } = usePermissions();
  const { data: openSessionData } = useGetOpenSessionQuery(
    { restaurantId: currentRestaurant?.id ?? "" },
    { skip: !currentRestaurant?.id }
  );
  const hasOpenSession = !!openSessionData;
  const { durations, isTerminal, currentColorClass } =
    useOrderTimingBreakdown(order);

  const totalPrice = useMemo(() => getOrderTotal(order), [order]);

  const userRole = currentUser?.roles?.includes(Roles.kitchen)
    ? Roles.kitchen
    : undefined;
  const isPending = order.status === "pending";

  // Capturing a payment is permission-gated (payments.capture) — cashiers,
  // managers and delegated staff hold it; waiters do not.
  const canProcessPayment = can("payments.capture");

  const canPayFromCard =
    !order.isPaid &&
    canProcessPayment &&
    ["served", "picked_up", "delivered"].includes(order.status);

  const handleStatusChange = async (
    e: React.MouseEvent,
    newStatus: OrderStatus
  ) => {
    e.stopPropagation();
    if (newStatus === "accepted" && !hasOpenSession) {
      toast.error(
        "Aucune session de caisse ouverte. Veuillez ouvrir une session avant d'accepter les commandes."
      );
      return;
    }
    // An order holding collected money can't be canceled/rejected until it
    // is refunded (SFAPI-114).
    if (await blockIfCollected(order, newStatus)) return;
    const confirmation = await confirmStatusChange(order, newStatus);
    if (!confirmation.proceed) return;
    try {
      await changeOrderStatus({ id: order.id, status: newStatus }).unwrap();
      dispatch(OrderApi.util.invalidateTags(["order-list"]));
    } catch (error) {
      if (isCollectedMoneyViolation(error)) {
        dispatch(OrderApi.util.invalidateTags(["order-list"]));
        await showBlocked(newStatus, null);
        return;
      }
      toast.error("Erreur lors du changement de statut");
      logger.error("OrderCard", "Error changing order status:", error);
    }
  };

  // Status changes need orders.manage; staff who can only view orders get a
  // read-only card (SFRESTO-184).
  const canManageOrders = canAny(...ORDERS_MANAGE_PERMISSIONS);
  const availableTransitions = canManageOrders
    ? (order.availableTransitions ?? [])
    : [];

  const showRestaurantName =
    currentUser?.roles?.includes(Roles.owner) && !currentRestaurant?.id;

  const allItems = [
    ...order.orderItems.map((item) => ({
      name: item.dailyDish?.name ?? item.menuItem?.title ?? "Article",
      qty: item.quantity,
    })),
    ...(order.menuComboOrderItems ?? []).map((item) => ({
      name: item.menuCombo?.name ?? "Combo",
      qty: item.quantity,
    })),
    ...(order.menuFormulaOrderItems ?? []).map((item) => ({
      name: item.menuFormula?.name ?? "Formule",
      qty: item.quantity,
    })),
  ];
  const previewItems = allItems.slice(0, MAX_PREVIEW_ITEMS);
  const remainingCount = allItems.length - previewItems.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full"
    >
      <div
        className={cn(
          "bg-card rounded-lg border border-border shadow-sm transition-all h-full flex flex-col cursor-pointer hover:shadow-md",
          isPending && "ring-2 ring-primary ring-opacity-50"
        )}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (!target.closest("[data-no-card-click]")) {
            setIsDetailsOpen(true);
            onModalOpen?.();
          }
        }}
      >
        <div className="p-4 flex flex-col flex-1">
          {/* Header row 1: icon + order info + price */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="bg-primary-light text-primary font-bold text-xl rounded-lg w-12 h-12 flex items-center justify-center flex-shrink-0">
                {order.orderType === "takeaway" ? (
                  <Package className="h-6 w-6" />
                ) : order.orderType === "delivery" ? (
                  <Bike className="h-6 w-6" />
                ) : (
                  order.restaurantTable?.code ?? "?"
                )}
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-card-foreground text-sm truncate">
                  #{highlightText(order.orderNumber, searchOrderNumber)}
                </h3>
                {showRestaurantName && order.restaurant?.name && (
                  <p className="text-xs text-primary font-medium truncate">
                    {order.restaurant.name}
                  </p>
                )}
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDate(new Date(order.createdAt))}
                </p>
              </div>
            </div>

            <span className="text-lg font-semibold text-card-foreground whitespace-nowrap flex-shrink-0">
              {formatPrice(totalPrice / 100, currency)}
            </span>
          </div>

          {/* Header row 2: status + payment + type badges (wrap-friendly) */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Badge status={order.status}>
              {getOrderStatusText(order.status, userRole)}
            </Badge>
            {order.isPaid && (
              <Badge
                variant="outline"
                className="ring-success text-success border-success"
              >
                Payée
              </Badge>
            )}
            {order.orderType && order.orderType !== "dine_in" && (
              <span className="text-xs font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                {getOrderTypeLabel(order.orderType)}
              </span>
            )}
          </div>

          {/* Dashed separator top */}
          <div className="border-t border-dashed border-border" />

          {/* Items preview - fixed height for 3 items */}
          <div className="py-3 space-y-2.5 min-h-[100px]">
            {previewItems.map((item, i) => (
              <div
                key={i}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-muted-foreground truncate">
                  {item.name}
                </span>
                <span className="text-muted-foreground flex-shrink-0 ml-2">
                  x{item.qty}
                </span>
              </div>
            ))}
            {remainingCount > 0 && (
              <p className="text-xs text-muted-foreground">
                +{remainingCount} autre{remainingCount > 1 ? "s" : ""}
              </p>
            )}
          </div>

          {/* Dashed separator bottom */}
          <div className="border-t border-dashed border-border" />

          {/* Footer: timing breakdown + actions */}
          {!isTerminal && durations.length > 0 && (
            <div className="pt-3 mt-auto text-center">
              {durations.length > 1 && (
                <p className="text-xs text-muted-foreground mb-0.5 truncate">
                  {durations
                    .slice(0, -1)
                    .map(
                      (d) =>
                        `${getOrderStatusText(d.status, userRole)}: ${formatDuration(d.durationMs)}`
                    )
                    .join(" • ")}
                </p>
              )}
              <p className={cn("text-lg font-semibold", currentColorClass)}>
                {formatDuration(durations[durations.length - 1].durationMs)}
              </p>
            </div>
          )}
          {!readonly && (availableTransitions.length > 0 || canPayFromCard) && (
            <div
              className={`flex gap-2 pt-3 ${isTerminal ? "mt-auto" : ""}`}
              data-no-card-click
            >
              {availableTransitions.map((transition) => (
                <Button
                  key={transition}
                  variant="outline"
                  size="default"
                  className={`flex-1 text-sm font-semibold ${STATUS_BUTTON_STYLES[transition] ?? ""}`}
                  onClick={(e) => handleStatusChange(e, transition)}
                  isLoading={isChangingStatus}
                >
                  {getOrderActionText(transition)}
                </Button>
              ))}
              {canPayFromCard && (
                <Button
                  size="default"
                  className="flex-1 text-sm font-semibold"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPaymentModalOpen(true);
                    onModalOpen?.();
                  }}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Payer
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {isDetailsOpen && (
        <OrderDetailsDrawer
          orderId={order.id}
          orderNumber={order.orderNumber}
          isOpen={isDetailsOpen}
          onClose={() => {
            setIsDetailsOpen(false);
            onModalClose?.();
          }}
        />
      )}

      {isPaymentModalOpen && (
        <PaymentModal
          order={order}
          isOpen={isPaymentModalOpen}
          onClose={() => {
            setIsPaymentModalOpen(false);
            onModalClose?.();
            dispatch(OrderApi.util.invalidateTags(["order-list"]));
          }}
        />
      )}
    </motion.div>
  );
};

export default OrderCard;
